import { EntityEnvironment, bridge } from "@dinahmoe/dmafjs-core";

import { visualEntities } from "../entities.js";
import { attachRoot } from "../helpers/elements.helper.js";
import { addToPendingLayout, cssValue } from "../helpers/styles.helper.js";
import { visualWebEngine } from "../visual-web-engine.js";
import { visualSuperHtml } from "./visual-super.environment.js";
import { focusManager } from "../utils/focus.util.js";

const body = typeof document !== "undefined" ? document.querySelector("body") : null;

class RootHtml extends EntityEnvironment {
  handlers = this.extendParentHandlers({
    "size.width": this.setWidth,
    "size.height": this.setHeight,
    "appearance.background": this.setBackgroundColor,
  });

  constructor(...arguments_) {
    super(...arguments_);
    if (typeof window !== "undefined") {
      window.addEventListener("contextmenu", (e) => {
        if (e.target.style?.pointerEvents === "auto") return;
        e.preventDefault();
      }, true);
    }
  }

  create(eNode) {
    eNode.depth = 0;

    for (const key of Object.keys(eNode.patch)) {
      eNode.props[key] = eNode.patch[key];
    }
    eNode.patch = {};

    eNode.element = attachRoot(eNode);

    screen.orientation.addEventListener("change", (event) => this.onOrientationChange(eNode));
    this.onOrientationChange(eNode)

    this.setIsEmbedded(eNode);

    document.addEventListener("visibilitychange", (event) => this.onVisibilityChange(eNode));
    this.onVisibilityChange(eNode)

    window.addEventListener("focus", (event) => this.onWindowFocus(eNode));
    window.addEventListener("blur", (event) => this.onWindowBlur(eNode));
    this.onWindowFocus(eNode)

    this.setViewport(eNode);
    this.setBackgroundColor(eNode);

    window.addEventListener("resize", this.onResize.bind(this, eNode));
    this.onResize(eNode);

    // TODO: Deferred resize to pick up viewport meta change on mobile.
    // The viewport meta tag change doesn't apply synchronously, so
    // window.innerWidth still returns the device width on the first read.
    setTimeout(() => this.onResize(eNode), 100);

    window.addEventListener("keydown", this.onKeyEvent.bind(this, eNode, "down"));
    window.addEventListener("keyup", this.onKeyEvent.bind(this, eNode, "up"));

    focusManager.setupRootComponent(eNode.uid);
    document.addEventListener("mousedown", this.handleFocus, true);

    // Auto-focus window on load to enable keyboard events
    // This ensures keyboard shortcuts work without requiring a click after page reload
    setTimeout(() => {
      if (document.hasFocus && !document.hasFocus()) {
        window.focus();
      }
    }, 0);

    eNode.element.style.overflow = "hidden";

    addToPendingLayout(eNode);

    const isSafariDesktop =
      navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome");
    if (isSafariDesktop) {
      document.documentElement.style.overflow = "hidden";
    }

    // Expose root node for static HTML export
    window.__dmafRootNode__ = eNode;
  }

  disableBodyScroll() {
    body.style.overflowX = "hidden";
    body.style.overflowY = "hidden";
  }

  setWidth(eNode) {
    addToPendingLayout(eNode);
    body.style.overflowX = eNode.props["size.width"] <= eNode.props["size.widthMin"] ? "scroll" : "hidden";
  }

  setHeight(eNode) {
    addToPendingLayout(eNode);
    body.style.overflowY = eNode.props["size.height"] <= eNode.props["size.heightMin"] ? "scroll" : "hidden";
  }

  onKeyEvent(eNode, type, event) {
    if (!event.key) {
      return;
    }

    const key = event.key.toLowerCase();
    bridge.update({
      uid: eNode.uid,
      key: "keyevent",
      value: {
        id: eNode.props["id"],
        key,
        type
      },
      sender: "browser",
    });
  }

  handleFocus(event) {
    const allFocusableNodes = focusManager.getFocusableNodes();
    const targetUids = [];

    for (const node of allFocusableNodes) {
      if (node.element.contains(event.target)) {
        targetUids.push(node.uid);
      }
    }

    const { uids, options } = focusManager.getFocusOptions(targetUids);
    focusManager.setFocused(uids, options);
  }

  focusNodes(eNode, { uids }) {
    for (const uid of uids) {
      bridge.update({ uid, key: "focus", sender: "browser" });
    }
  }

  unfocusNodes(eNode, { uids }) {
    for (const uid of uids) {
      bridge.update({ uid, key: "unfocus", sender: "browser" });
    }
  }

  update(eNode, config) {
    visualSuperHtml.update.call(this, eNode, config);
  }

  onVisibilityChange(eNode) {
    eNode.visibility = document.visibilityState;
    visualWebEngine.setVisibility(eNode.visibility);

    bridge.update({
      uid: eNode.uid,
      key: "visibility",
      value: {
        id: eNode.props["id"],
        visibility: eNode.visibility,
      },
      sender: "browser",
    });
  }

  onWindowFocus(eNode) {
    eNode.windowFocused = document.hasFocus();
    visualWebEngine.setWindowFocus(eNode.windowFocused);

    bridge.update({
      uid: eNode.uid,
      key: "windowfocus",
      value: {
        id: eNode.props["id"],
        focused: eNode.windowFocused,
      },
      sender: "browser",
    });
  }

  onWindowBlur(eNode) {
    eNode.windowFocused = false;
    visualWebEngine.setWindowFocus(false);

    bridge.update({
      uid: eNode.uid,
      key: "windowfocus",
      value: {
        id: eNode.props["id"],
        focused: false,
      },
      sender: "browser",
    });
  }

  onResize(eNode) {
    const width = window.innerWidth;
    const height = window.innerHeight;
    bridge.update({
      uid: eNode.uid,
      key: "resize",
      value: {
        id: eNode.props["id"],
        width: width,
        height: height,
      },
      sender: "browser",
    });
  }

  onOrientationChange(eNode) {
    eNode.props["orientation.type"] = screen.orientation.type;
    eNode.props["orientation.angle"] = screen.orientation.angle;

    bridge.update({
      uid: eNode.uid,
      key: "orientation",
      value: {
        id: eNode.props["id"],
        type: eNode.props["orientation.type"],
        angle: eNode.props["orientation.angle"]
      },
      sender: "browser",
    });
  }

  applyLayout(eNode) {
    eNode.element.style.width = cssValue(eNode.props["size.width"]);
    eNode.element.style.height = cssValue(eNode.props["size.height"]);
  }

  applyTransforms() {
    // TODO required for recursive css update in dom
    // should be solved in another way
  }

  // Checks if window is opened through IFrame for example
  setIsEmbedded(eNode) {
    const isEmbedded = window.location !== window.parent.location;

    bridge.update({
      uid: eNode.uid,
      id: eNode.props["id"],
      key: "embedded",
      value: {
        isEmbedded,
      },
      sender: "browser",
    });
  }

  setViewport(eNode) {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) viewport.content = `width=${eNode.props["size.widthMobile"]}`;
  }

  setBackgroundColor(eNode) {
    const rootElement = document.querySelector("#root");
    rootElement.style.backgroundColor = eNode.props["appearance.background"];
  }
}

const rootHtml = new RootHtml("visual:root");
visualEntities.set("visual:root", rootHtml);
export { RootHtml, rootHtml };
